import React from 'react';
import { AuthorisedLink, useApplication, useLocalStorage } from '@economic/react-app';
import { Hanger, Icon } from '@economic/taco';
import { Trans } from '@lingui/macro';
import useCachedQuery from '~/app/shared/hooks/useCachedQuery';
import { CustomTab } from '~/types';

type PrimaryNavigationLinksProps<T extends React.ElementType, L extends React.ElementType> = {
    as: T;
    linkAs: L;
    linkProps?: React.ComponentProps<L>;
    showNesting?: boolean;
} & React.ComponentProps<T>;
export const PrimaryNavigationLinks = <T extends React.ElementType, L extends React.ElementType>(
    props: PrimaryNavigationLinksProps<T, L>
) => {
    const { as: Tag, linkAs, linkProps = {}, showNesting = false, ...attributes } = props;
    const { routes } = useApplication();
    const customTabsQuery = useCachedQuery<CustomTab[]>('/user/tabs/custom', []);
    const [expenseHangerViewed, setExpenseHangerViewed] = useLocalStorage('hangers.expenseNavLink', false);
    const nestingArrow = showNesting ? <Icon name="chevron-right" className="ml-auto -mr-1" /> : null;

    // we have to create the links with if statements like this so that non authorised children aren't passed to the
    // underlying component - it breaks the "more menu" because it can't detect null children until after rendering
    const linkElements = React.useMemo(() => {
        const links: React.JSX.Element[] = [];

        if (routes.hasOwnProperty('/system')) {
            links.push(
                <AuthorisedLink key="system" to="system" as={linkAs} {...linkProps}>
                    <Trans>System</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/home')) {
            links.push(
                <AuthorisedLink key="home" to="home" as={linkAs} name="home" {...linkProps}>
                    <Trans>Home</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/basic')) {
            links.push(
                <AuthorisedLink key="basic" to="basic" as={linkAs} {...linkProps}>
                    <Trans>Basic</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/developer')) {
            links.push(
                <AuthorisedLink key="developer" to="developer" as={linkAs} {...linkProps}>
                    <Trans>Apps</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/partner-api')) {
            links.push(
                <AuthorisedLink key="partner-api" to="partner-api" as={linkAs} {...linkProps}>
                    <Trans>Partner API</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/sales')) {
            links.push(
                <AuthorisedLink key="sales" to="sales" as={linkAs} name="sales" {...linkProps}>
                    <Trans>Sales</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/expenses')) {
            links.push(
                expenseHangerViewed ? (
                    <AuthorisedLink key="expenses" to="expenses" as={linkAs} name="expenses" {...linkProps}>
                        <Trans id="TOP_NAVIGATION_EXPENSES_TAB">Expenses</Trans>
                    </AuthorisedLink>
                ) : (
                    <Hanger defaultOpen onClose={() => setExpenseHangerViewed(true)}>
                        <Hanger.Anchor>
                            <AuthorisedLink key="expenses" to="expenses" as={linkAs} name="expenses" {...linkProps}>
                                <Trans id="TOP_NAVIGATION_EXPENSES_TAB">Expenses</Trans>
                            </AuthorisedLink>
                        </Hanger.Anchor>
                        <Hanger.Content title="New tab for expenses">
                            <Hanger.Title>
                                <Trans>New tab for expenses</Trans>
                            </Hanger.Title>
                            <Trans>
                                Get a better overview of payments and documents pending your approval.{' '}
                                <a href="https://www.e-conomic.dk/funktioner/udgiftshaandtering" target="_blank">
                                    Read more.
                                </a>
                            </Trans>
                        </Hanger.Content>
                    </Hanger>
                )
            );
        }
        if (routes.hasOwnProperty('/accounting')) {
            links.push(
                <AuthorisedLink key="accounting" to="accounting" as={linkAs} name="accounting" {...linkProps}>
                    <Trans>Accounting</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }

        if (routes.hasOwnProperty('/reports')) {
            links.push(
                <AuthorisedLink key="reports" to="reports" as={linkAs} name="reports" {...linkProps}>
                    <Trans>Reports</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/projects')) {
            links.push(
                <AuthorisedLink key="projects" to="projects" as={linkAs} {...linkProps}>
                    <Trans>Projects</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/website')) {
            links.push(
                <AuthorisedLink key="website" to="website" as={linkAs} {...linkProps}>
                    <Trans>Website</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/webshop')) {
            links.push(
                <AuthorisedLink key="webshop" to="webshop" as={linkAs} {...linkProps}>
                    <Trans>Webshop</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/webshop-plus')) {
            links.push(
                <AuthorisedLink key="webshop-plus" to="webshop-plus" as={linkAs} {...linkProps}>
                    <Trans>Webshop</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/payroll')) {
            links.push(
                <AuthorisedLink key="payroll" to="payroll" as={linkAs} {...linkProps}>
                    <Trans>Payroll</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/charlie')) {
            links.push(
                <AuthorisedLink key="charlie" to="charlie" as={linkAs} {...linkProps}>
                    <Trans>Charlie</Trans>
                </AuthorisedLink>
            );
        }
        if (customTabsQuery.data?.length) {
            customTabsQuery.data.forEach(customTab => {
                if (routes.hasOwnProperty('/custom')) {
                    links.push(
                        <AuthorisedLink key={customTab.url} to={`custom?uri=${customTab.url}`} as={linkAs} {...linkProps}>
                            {customTab.name}
                        </AuthorisedLink>
                    );
                }
            });
        }

        return links;
    }, [customTabsQuery.data?.length, expenseHangerViewed]);

    return (
        <Tag {...attributes} key={customTabsQuery.loading}>
            {linkElements}
        </Tag>
    );
};
